import React, { useState, useEffect } from 'react';
import PageIdentifier from './shared/PageIdentifier';
import SCREEN_IDS from '../utils/screenIds';

const SharedInvoiceView = ({ invoiceId: propInvoiceId }) => {
  // Get invoiceId from props or URL
  const invoiceId = propInvoiceId || window.location.pathname.split('/')[2];
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      // Relative path — same-origin in every environment. A hardcoded
      // 127.0.0.1:3000 here only works on this machine; any real customer's
      // browser (opening the link from WhatsApp) can never reach that.
      const response = await fetch(`/api/invoices/shared/${invoiceId}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load invoice');
      }

      const data = await response.json();
      setInvoice(data.invoice);
      setError(null);
    } catch (err) {
      console.error('Error fetching invoice:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (amount, currency) => {
    if (!amount) return '0.00';
    const symbol = currency === 'INR' ? '₹' : '$';
    const numAmount = typeof amount === 'string' 
      ? parseFloat(amount.replace(/[₹$,]/g, '')) 
      : amount;
    return `${symbol} ${numAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Invoice Not Found</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">
            This invoice may have expired or the link may be incorrect.
          </p>
        </div>
      </div>
    );
  }

  const { data, type, currency, createdAt, expiresAt } = invoice;
  const isInvoice = type === 'invoice';
  const lineItems = Array.isArray(data.items) ? data.items : [];
  const isMultiItem = lineItems.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <PageIdentifier pageId={SCREEN_IDS.SHARED.INVOICE_VIEW} pageName="Shared Invoice View" />
      {/* Print Styles */}
      <style>{`
        @media print {
          body { 
            background: white !important; 
            padding: 0 !important;
            margin: 0 !important;
          }
          .no-print { display: none !important; }
          .print-container { 
            max-width: none !important; 
            margin: 0 !important;
            box-shadow: none !important;
            padding: 20px !important;
          }
        }
      `}</style>

      <div className="container mx-auto px-4 max-w-4xl">
        {/* Action Buttons - Hidden in Print */}
        <div className="no-print mb-6 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            <p>Shared on: {formatDate(createdAt)}</p>
            <p>Expires: {formatDate(expiresAt)}</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handlePrint}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <span>🖨️</span>
              Print
            </button>
          </div>
        </div>

        {/* Invoice/Estimate Document */}
        <div className="print-container bg-white shadow-lg rounded-lg p-8">
          {/* Header */}
          <div className="border-b-2 border-gray-800 pb-6 mb-6">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold text-gray-800 mb-2">
                  {data.companyName || 'GDS Jewellers'}
                </h1>
                <p className="text-gray-600">
                  {data.companyAddress || 'Your Store Address Here'}
                </p>
                <p className="text-gray-600">
                  Phone: {data.companyPhone || 'Contact Number'}
                </p>
                {data.companyGSTIN && (
                  <p className="text-gray-600">GSTIN: {data.companyGSTIN}</p>
                )}
              </div>
              <div className="text-right">
                <h2 className="text-2xl font-bold text-blue-600 mb-2">
                  {isInvoice ? 'INVOICE' : 'ESTIMATE'}
                </h2>
                <p className="text-gray-600">
                  {isInvoice ? 'Invoice #' : 'Estimate #'}: {data.invoiceNumber || invoiceId}
                </p>
                <p className="text-gray-600">
                  Date: {data.date || formatDate(createdAt)}
                </p>
                {!isInvoice && data.validUntil && (
                  <p className="text-gray-600">
                    Valid Until: {data.validUntil}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Bill To:</h3>
            <div className="bg-gray-50 p-4 rounded">
              <p className="font-semibold text-gray-800">{data.customerName}</p>
              {data.phoneNumber && <p className="text-gray-600">Phone: {data.phoneNumber}</p>}
              {data.address && <p className="text-gray-600">{data.address}</p>}
            </div>
          </div>

          {/* Item Details */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Item Details:</h3>
            {isMultiItem ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-800 text-white">
                    <tr>
                      <th className="px-4 py-2 text-left">#</th>
                      <th className="px-4 py-2 text-left">Item</th>
                      <th className="px-4 py-2 text-center">Net Wt</th>
                      <th className="px-4 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((it, i) => (
                      <tr key={i} className="border-b">
                        <td className="px-4 py-2 text-gray-500">{i + 1}</td>
                        <td className="px-4 py-2 font-semibold">
                          {it.itemCode || '—'}{it.purity ? ` · ${it.purity}` : ''}
                        </td>
                        <td className="px-4 py-2 text-center">{it.netWeight ? `${it.netWeight} g` : '—'}</td>
                        <td className="px-4 py-2 text-right font-semibold">{formatCurrency(it.amount, currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <tbody className="text-sm">
                  {data.itemCode && (
                    <tr className="border-b">
                      <td className="px-4 py-2 bg-gray-50 font-semibold">Item Code</td>
                      <td className="px-4 py-2">{data.itemCode}</td>
                    </tr>
                  )}
                  {data.itemName && (
                    <tr className="border-b">
                      <td className="px-4 py-2 bg-gray-50 font-semibold">Item Name</td>
                      <td className="px-4 py-2">{data.itemName}</td>
                    </tr>
                  )}
                  {data.category && (
                    <tr className="border-b">
                      <td className="px-4 py-2 bg-gray-50 font-semibold">Category</td>
                      <td className="px-4 py-2">{data.category} {data.subCategory && `- ${data.subCategory}`}</td>
                    </tr>
                  )}
                  {data.itemType && (
                    <tr className="border-b">
                      <td className="px-4 py-2 bg-gray-50 font-semibold">Type</td>
                      <td className="px-4 py-2">{data.itemType}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            )}
          </div>

          {/* Gold Details */}
          {data.goldWeight && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Gold Details:</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <tbody>
                    {data.goldType && (
                      <tr className="border-b">
                        <td className="px-4 py-2 bg-gray-50 font-semibold">Gold Type</td>
                        <td className="px-4 py-2">{data.goldType}</td>
                      </tr>
                    )}
                    <tr className="border-b">
                      <td className="px-4 py-2 bg-gray-50 font-semibold">Weight</td>
                      <td className="px-4 py-2">{data.goldWeight}</td>
                    </tr>
                    {data.goldRate && (
                      <tr className="border-b">
                        <td className="px-4 py-2 bg-gray-50 font-semibold">Rate</td>
                        {/* goldRate is always sourced from the INR gold_rates table,
                            regardless of the invoice's billing currency. */}
                        <td className="px-4 py-2">{formatCurrency(data.goldRate, 'INR')}</td>
                      </tr>
                    )}
                    {data.makingCharges && (
                      <tr className="border-b">
                        <td className="px-4 py-2 bg-gray-50 font-semibold">Making Charges</td>
                        <td className="px-4 py-2">{formatCurrency(data.makingCharges, currency)}</td>
                      </tr>
                    )}
                    {data.goldAmount && (
                      <tr className="bg-blue-50">
                        <td className="px-4 py-2 font-bold">Gold Amount</td>
                        <td className="px-4 py-2 font-bold">{formatCurrency(data.goldAmount, currency)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Stone Details */}
          {data.stoneWeight && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Stone Details:</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <tbody>
                    {data.stoneType && (
                      <tr className="border-b">
                        <td className="px-4 py-2 bg-gray-50 font-semibold">Stone Type</td>
                        <td className="px-4 py-2">{data.stoneType}</td>
                      </tr>
                    )}
                    <tr className="border-b">
                      <td className="px-4 py-2 bg-gray-50 font-semibold">Weight</td>
                      <td className="px-4 py-2">{data.stoneWeight}</td>
                    </tr>
                    {data.stoneRate && (
                      <tr className="border-b">
                        <td className="px-4 py-2 bg-gray-50 font-semibold">Rate</td>
                        <td className="px-4 py-2">{formatCurrency(data.stoneRate, currency)}</td>
                      </tr>
                    )}
                    {data.stoneAmount && (
                      <tr className="bg-blue-50">
                        <td className="px-4 py-2 font-bold">Stone Amount</td>
                        <td className="px-4 py-2 font-bold">{formatCurrency(data.stoneAmount, currency)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Amount Breakdown */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Amount Details:</h3>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {data.subTotal && (
                    <tr className="border-b">
                      <td className="px-4 py-3 font-semibold">Sub Total</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(data.subTotal, currency)}</td>
                    </tr>
                  )}
                  {data.otherCharges && parseFloat(data.otherCharges.replace(/[₹$,]/g, '')) > 0 && (
                    <tr className="border-b">
                      <td className="px-4 py-3 font-semibold">Other Charges</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(data.otherCharges, currency)}</td>
                    </tr>
                  )}
                  {data.discount && parseFloat(data.discount.replace(/[₹$,]/g, '')) > 0 && (
                    <tr className="border-b text-red-600">
                      <td className="px-4 py-3 font-semibold">Round Off</td>
                      <td className="px-4 py-3 text-right">- {formatCurrency(data.discount, currency)}</td>
                    </tr>
                  )}
                  {currency === 'INR' && (
                    <>
                      {data.cgst && (
                        <tr className="border-b">
                          <td className="px-4 py-3 font-semibold">CGST</td>
                          <td className="px-4 py-3 text-right">{formatCurrency(data.cgst, currency)}</td>
                        </tr>
                      )}
                      {data.sgst && (
                        <tr className="border-b">
                          <td className="px-4 py-3 font-semibold">SGST</td>
                          <td className="px-4 py-3 text-right">{formatCurrency(data.sgst, currency)}</td>
                        </tr>
                      )}
                      {data.totalGst && (
                        <tr className="border-b">
                          <td className="px-4 py-3 font-semibold">Total GST</td>
                          <td className="px-4 py-3 text-right">{formatCurrency(data.totalGst, currency)}</td>
                        </tr>
                      )}
                    </>
                  )}
                  <tr className="bg-blue-600 text-white">
                    <td className="px-4 py-4 text-lg font-bold">Grand Total</td>
                    <td className="px-4 py-4 text-lg font-bold text-right">{formatCurrency(data.grandTotal, currency)}</td>
                  </tr>
                  {isInvoice && data.advancePayment && parseFloat(data.advancePayment.replace(/[₹$,]/g, '')) > 0 && (
                    <>
                      <tr className="border-b">
                        <td className="px-4 py-3 font-semibold">Advance Payment</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(data.advancePayment, currency)}</td>
                      </tr>
                      <tr className="bg-green-50">
                        <td className="px-4 py-3 font-bold text-green-700">Balance Due</td>
                        <td className="px-4 py-3 font-bold text-green-700 text-right">{formatCurrency(data.balancePayment, currency)}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes and Terms */}
          {(data.notes || data.terms) && (
            <div className="mt-8 pt-6 border-t">
              {data.notes && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-1">Notes:</h3>
                  <p className="text-sm text-gray-600">{data.notes}</p>
                </div>
              )}
              {data.terms && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-1">Terms & Conditions:</h3>
                  <p className="text-sm text-gray-600">{data.terms}</p>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 pt-6 border-t text-center text-sm text-gray-500">
            <p>Thank you for your business!</p>
            <p className="mt-2">
              This is a {isInvoice ? 'computer-generated invoice' : 'price estimate'} and does not require a signature.
            </p>
          </div>
        </div>

        {/* Info Message - Hidden in Print */}
        <div className="no-print mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            💡 <strong>Tip:</strong> You can print this {isInvoice ? 'invoice' : 'estimate'} using the Print button above 
            or save it as PDF using your browser's print function (Ctrl+P or Cmd+P).
          </p>
        </div>
      </div>
    </div>
  );
};

export default SharedInvoiceView;
